import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, Check, X, Loader2, AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABELS = {
  1: { title: 'Re-onboard Request', roles: ['BFR', 'CX Leader'] },
  2: { title: 'Confirmation', roles: ['Opposite of Step 1'] },
  3: { title: 'Final Approval', roles: ['Executive', 'Admin'] },
};

/**
 * Phase 19.1 — Re-onboard 3-step workflow card.
 *
 * Visible only to bfr / cx_leader / executive / admin on fully-offboarded
 * properties. Renders the current state of the additive `reonboarding`
 * sub-document on `property_lifecycle` and exposes per-step actions:
 *   Step 1: bfr or cx_leader → Re-onboard | Cancel Re Onboarding
 *   Step 2: OPPOSITE role of step 1 → Re-onboard | Cancel Re Onboarding
 *   Step 3: executive or admin → Re-onboard | Cancel Re Onboarding
 */
const ReonboardCard = ({ hotelId, onRefresh }) => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const role = (user?.role || '').toLowerCase();
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');

  const fetchState = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/lifecycle/${hotelId}/reonboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.ok) setState(await r.json());
    } catch (_) { /* swallow — card hides on error */ }
    finally { setLoading(false); }
  }, [hotelId, token]);

  useEffect(() => { fetchState(); }, [fetchState]);

  const submit = async (stepNo, decision) => {
    setActing(true);
    try {
      const url = `${API}/api/lifecycle/${hotelId}/reonboard/step?step_no=${stepNo}&decision=${encodeURIComponent(decision)}`;
      const r = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const errBody = await r.text();
        let msg = `Failed (${r.status})`;
        try { msg = JSON.parse(errBody)?.detail || msg; } catch { msg = errBody || msg; }
        toast.error('Re-onboard action failed', { description: msg });
        return;
      }
      const body = await r.json();
      setState(prev => ({ ...(prev || {}), reonboarding: body.reonboarding, lifecycleStatus: body.status === 'completed' ? 'onboarded' : prev?.lifecycleStatus }));
      if (decision === 'Re-onboard' && stepNo === 3) {
        toast.success('Property re-onboarded', { description: 'Lifecycle restored to Onboarded.' });
        onRefresh?.();
      } else if (decision === 'Cancel Re Onboarding') {
        toast.info('Re-onboarding cancelled', { description: 'Property remains Offboarded.' });
      } else {
        toast.success(`Step ${stepNo} approved`);
      }
    } finally {
      setActing(false);
    }
  };

  if (loading) return null;
  if (!state) return null;
  // Hide card for unauthorised roles entirely.
  if (!state.can_view_button) return null;
  // Hide if property is not in a re-onboard-eligible state (i.e. it's not
  // offboarded). The backend already returns lifecycleStatus on this payload.
  const isOffboarded = state.lifecycleStatus === 'offboarded' || (state.propertyStatus || '').toLowerCase() === 'inactive';
  const isReonboarded = state.reonboarding?.status === 'completed';
  if (!isOffboarded && !isReonboarded) return null;

  const reonboarding = state.reonboarding || { status: 'none', steps: [] };
  const completedSteps = new Set(
    (reonboarding.steps || []).filter(s => s.decision === 'Re-onboard').map(s => s.step_no),
  );
  const cancelled = reonboarding.status === 'cancelled';
  const completed = reonboarding.status === 'completed';

  // Compute who is permitted at each step (UI-side mirror of backend gating).
  const step1Role = state.step1_role;
  const canActOn = (stepNo) => {
    if (acting || cancelled || completed) return false;
    if (role === 'admin') return stepNo === 1 ? !cancelled : (stepNo === 2 ? completedSteps.has(1) : completedSteps.has(2));
    if (stepNo === 1) return ['bfr', 'cx_leader'].includes(role) && !completedSteps.has(1);
    if (stepNo === 2) {
      if (!completedSteps.has(1) || !step1Role) return false;
      const opposite = step1Role === 'bfr' ? 'cx_leader' : 'bfr';
      return role === opposite && !completedSteps.has(2);
    }
    if (stepNo === 3) {
      return ['executive', 'admin'].includes(role) && completedSteps.has(2) && !completedSteps.has(3);
    }
    return false;
  };

  const StepRow = ({ stepNo }) => {
    const meta = STEP_LABELS[stepNo];
    const stepEntry = (reonboarding.steps || []).filter(s => s.step_no === stepNo).slice(-1)[0];
    const isDone = completedSteps.has(stepNo);
    const isCancelledHere = stepEntry?.decision === 'Cancel Re Onboarding';
    const enabled = canActOn(stepNo);
    return (
      <div className={`flex items-start gap-3 py-3 ${stepNo < 3 ? 'border-b border-gray-100' : ''}`} data-testid={`reonboard-step-${stepNo}`}>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
          isDone ? 'bg-emerald-500 text-white' : isCancelledHere ? 'bg-red-500 text-white' : enabled ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          {isDone ? <Check size={14} /> : isCancelledHere ? <X size={14} /> : stepNo}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800">{meta.title}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Allowed: {meta.roles.join(' / ')}</p>
          {stepEntry && (
            <p className="text-[11px] text-gray-600 mt-1 flex items-center gap-1.5">
              <Clock size={11} />
              {stepEntry.decision} · {stepEntry.by_user_name} ({stepEntry.by_role}) · {new Date(stepEntry.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          )}
          {enabled && (
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                onClick={() => submit(stepNo, 'Re-onboard')}
                disabled={acting}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
                data-testid={`reonboard-step-${stepNo}-approve`}
              >
                {acting ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Re-onboard
              </button>
              <button
                onClick={() => submit(stepNo, 'Cancel Re Onboarding')}
                disabled={acting}
                className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 disabled:opacity-50 flex items-center gap-1.5"
                data-testid={`reonboard-step-${stepNo}-cancel`}
              >
                <X size={12} /> Cancel Re Onboarding
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const headerBadgeClass = completed ? 'bg-emerald-100 text-emerald-700'
    : cancelled ? 'bg-red-100 text-red-700'
    : reonboarding.status === 'pending' ? 'bg-amber-100 text-amber-700'
    : 'bg-slate-100 text-slate-700';

  const headerBadgeText = completed ? 'Re-onboarded' : cancelled ? 'Cancelled' : reonboarding.status === 'pending' ? 'In Progress' : 'Available';

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="reonboard-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2">
          <RotateCcw size={18} className="text-teal-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-gray-800">Re-onboard Workflow</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">3-step approval to bring a fully-offboarded property back to Onboarded.</p>
          </div>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-1 rounded ${headerBadgeClass}`} data-testid="reonboard-status-badge">{headerBadgeText}</span>
      </div>

      {!isOffboarded && completed && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-700 flex items-center gap-2 mb-3" data-testid="reonboard-restored-banner">
          <Check size={14} /> Property restored — Onboarded · 100%
        </div>
      )}

      {cancelled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-center gap-2 mb-3" data-testid="reonboard-cancelled-banner">
          <AlertCircle size={14} /> Last re-onboard attempt was cancelled. A new workflow can be started by BFR or CX Leader.
        </div>
      )}

      <div>
        <StepRow stepNo={1} />
        <StepRow stepNo={2} />
        <StepRow stepNo={3} />
      </div>
    </div>
  );
};

export default ReonboardCard;
